import { Maximize2, X } from 'lucide-react';
import { Dispatch, SetStateAction, useContext } from 'react';
import DivWithInert from '../../div-with-inert';
import { Button } from '../../ui/button';
import { ConfigContext } from '@/config/config-context';
import { cn } from '@/lib/utils';

/**
 * ChatWindowHeader component renders the header of the chat window.
 *
 * This component displays a header with buttons to expand the chat window and end the chat.
 * It also includes a title for the chat window.
 *
 * @param {object} props - The properties object.
 * @param {boolean} props.chatWindowEnd - A state indicating if the chat window end modal is displayed.
 * @param {Dispatch<SetStateAction<boolean>>} props.setChatWindowEnd - A function to update the chat window end state.
 * @param {Dispatch<SetStateAction<boolean>>} props.setChatWindowExpanded - A function to update the chat window expanded state.
 * @returns {JSX.Element} The ChatWindowHeader component.
 */
const ChatWindowHeader = ({
	chatWindowEnd,
	setChatWindowEnd,
	setChatWindowExpanded,
}: {
	chatWindowEnd: boolean;
	setChatWindowEnd: Dispatch<SetStateAction<boolean>>;
	setChatWindowExpanded: Dispatch<SetStateAction<boolean>>;
}): JSX.Element => {
	const config = useContext(ConfigContext);
	return (
		<DivWithInert
			inert={chatWindowEnd}
			className={cn(
				'wlcb-flex wlcb-items-center wlcb-justify-between wlcb-border-b-[1px] wlcb-border-b-stroke-subtle wlcb-py-space-075 wlcb-px-space-100 wlcb-gap-x-space-050 wlcb-w-full wlcb-rounded-t-sm',
				{
					'wlcb-bg-theme-brand-primary':
						config?.theme?.primary?.color !== undefined,
					'wlcb-bg-surface-brand': !config?.theme?.primary?.color,
				}
			)}
		>
			<span
				aria-hidden='true'
				className='md:wlcb-hidden wlcb-block wlcb-size-[48px]'
			></span>
			<Button
				className={cn('md:wlcb-flex wlcb-hidden', {
					'wlcb-bg-theme-brand-primary':
						config?.theme?.primary?.color !== undefined,
				})}
				aria-controls='chat-window'
				aria-label='Expandera chat fönstret'
				variant='ghost'
				size='lg'
				icon={{
					iconType: (
						<Maximize2
							aria-hidden='true'
							className={cn('wlcb-size-4 ', {
								'wlcb-stroke-[var(--theme-brand-primary-text-color)]':
									config?.theme?.primary !== undefined,
								'wlcb-stroke-white': !config?.theme?.primary,
							})}
						/>
					),
					orientation: 'default',
				}}
				onClick={() => setChatWindowExpanded((prev) => !prev)}
			/>
			<h2
				className={cn('wlcb-text-button  wlcb-select-none', {
					'wlcb-text-theme-brand-primary-text-color':
						config?.theme?.primary?.textColor,
					'wlcb-text-white': !config?.theme?.primary,
				})}
			>
				{config?.title ?? 'Ai support'}
			</h2>
			<Button
				variant='ghost'
				aria-label='Avsluta chat'
				aria-haspopup='dialog'
				aria-controls='chat-end'
				aria-expanded={chatWindowEnd}
				className='wlcb-bg-transparent'
				size='lg'
				icon={{
					iconType: (
						<X
							aria-hidden='true'
							className={cn('wlcb-size-6 ', {
								'wlcb-stroke-[var(--theme-brand-primary-text-color)]':
									config?.theme?.primary !== undefined,
								'wlcb-stroke-white': !config?.theme?.primary,
							})}
						/>
					),
					orientation: 'default',
				}}
				onClick={() => setChatWindowEnd(true)}
			/>
		</DivWithInert>
	);
};

export default ChatWindowHeader;
